import { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Camera, Upload, Loader2, Check, X, Sparkles, Plus, Minus, Save, RotateCcw, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

const MEAL_TYPES = [
  { id: 'breakfast', label: 'Breakfast', emoji: '🌅' },
  { id: 'lunch', label: 'Lunch', emoji: '☀️' },
  { id: 'dinner', label: 'Dinner', emoji: '🌙' },
  { id: 'snack', label: 'Snack', emoji: '🍎' }
];

export default function MealPhotoRecognition({ childId, onMealLogged }) {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState('upload'); // upload, analyzing, review, saving
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mealType, setMealType] = useState('lunch');
  const [analysis, setAnalysis] = useState(null);
  const [editedFoods, setEditedFoods] = useState([]);
  const [photoId, setPhotoId] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const resetState = () => {
    setStep('upload');
    setSelectedFile(null);
    setPreviewUrl(null);
    setAnalysis(null);
    setEditedFoods([]);
    setPhotoId(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image (JPEG, PNG, WebP)');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum 10MB');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const analyzePhoto = async () => {
    if (!selectedFile) return;

    setStep('analyzing');

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('meal_type', mealType);

    try {
      const res = await axios.post(`${API}/meal-vision/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setAnalysis(res.data);
      setEditedFoods(res.data.foods || []);
      setPhotoId(res.data.photo_id);
      setStep('review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to analyze photo');
      setStep('upload');
    }
  };

  const updateFood = (index, field, value) => {
    const updated = [...editedFoods];
    updated[index] = { ...updated[index], [field]: value };
    setEditedFoods(updated);
  };

  const removeFood = (index) => {
    setEditedFoods(editedFoods.filter((_, i) => i !== index));
  };

  const addManualFood = () => {
    setEditedFoods([
      ...editedFoods,
      {
        name: 'New item',
        portion_size: '1 serving',
        calories: 100,
        protein: 5,
        carbs: 15,
        fat: 3,
        fiber: 1,
        confidence: 'manual'
      }
    ]);
  };

  const calculateTotals = () => {
    return {
      calories: editedFoods.reduce((sum, f) => sum + (parseFloat(f.calories) || 0), 0),
      protein: editedFoods.reduce((sum, f) => sum + (parseFloat(f.protein) || 0), 0),
      carbs: editedFoods.reduce((sum, f) => sum + (parseFloat(f.carbs) || 0), 0),
      fat: editedFoods.reduce((sum, f) => sum + (parseFloat(f.fat) || 0), 0)
    };
  };

  const saveMeal = async () => {
    if (editedFoods.length === 0) {
      toast.error('Add at least one food item');
      return;
    }

    setStep('saving');

    const formData = new FormData();
    formData.append('child_id', childId);
    formData.append('photo_id', photoId || '');
    formData.append('meal_type', mealType);
    formData.append('date', new Date().toISOString());
    formData.append('foods_json', JSON.stringify(editedFoods));

    try {
      await axios.post(`${API}/meal-vision/save-to-log`, formData);
      toast.success('Meal logged successfully! 🎉');
      setShowModal(false);
      resetState();
      if (onMealLogged) onMealLogged();
    } catch (err) {
      toast.error('Failed to save meal');
      setStep('review');
    }
  };

  const totals = calculateTotals();

  return (
    <>
      <Button
        onClick={() => { resetState(); setShowModal(true); }}
        className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
        data-testid="photo-meal-btn"
      >
        <Camera className="h-4 w-4 mr-2" />
        Snap Meal
      </Button>

      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-white">AI Meal Scanner</h2>
                  <p className="text-xs text-zinc-400">
                    {step === 'upload' && 'Take or upload a photo of your meal'}
                    {step === 'analyzing' && 'Analyzing your meal...'}
                    {step === 'review' && 'Review and adjust detected foods'}
                    {step === 'saving' && 'Saving your meal...'}
                  </p>
                </div>
              </div>
              <button onClick={() => { setShowModal(false); resetState(); }} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 max-h-[70vh] overflow-y-auto">
              {/* Step 1: Upload */}
              {step === 'upload' && (
                <div className="space-y-4">
                  {/* Meal Type Selection */}
                  <div>
                    <label className="text-xs text-zinc-500 mb-2 block">Meal Type</label>
                    <div className="grid grid-cols-4 gap-2">
                      {MEAL_TYPES.map(type => (
                        <button
                          key={type.id}
                          onClick={() => setMealType(type.id)}
                          className={`p-3 rounded-lg text-center transition-all ${
                            mealType === type.id
                              ? 'bg-purple-500/20 border-2 border-purple-500 text-white'
                              : 'bg-zinc-800 border-2 border-transparent text-zinc-400 hover:text-white'
                          }`}
                        >
                          <span className="text-xl">{type.emoji}</span>
                          <p className="text-xs mt-1">{type.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Photo Upload Area */}
                  {!previewUrl ? (
                    <div className="border-2 border-dashed border-zinc-700 rounded-xl p-8">
                      <div className="text-center">
                        <ImageIcon className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                        <p className="text-white font-medium mb-2">Upload your meal photo</p>
                        <p className="text-xs text-zinc-500 mb-4">JPEG, PNG, or WebP up to 10MB</p>
                        
                        <div className="flex gap-3 justify-center">
                          <input
                            ref={cameraInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                          <Button
                            onClick={() => cameraInputRef.current?.click()}
                            className="bg-purple-500 hover:bg-purple-600"
                          >
                            <Camera className="h-4 w-4 mr-2" />
                            Take Photo
                          </Button>
                          
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                          <Button
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            className="border-zinc-700"
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Preview */}
                      <div className="relative rounded-xl overflow-hidden">
                        <img
                          src={previewUrl}
                          alt="Meal preview"
                          className="w-full h-48 object-cover"
                        />
                        <button
                          onClick={resetState}
                          className="absolute top-2 right-2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      </div>
                      
                      <Button
                        onClick={analyzePhoto}
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Analyze with AI
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Analyzing */}
              {step === 'analyzing' && (
                <div className="py-12 text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 mx-auto mb-6 flex items-center justify-center animate-pulse">
                    <Sparkles className="h-10 w-10 text-white" />
                  </div>
                  <p className="text-white font-medium mb-2">Analyzing your meal...</p>
                  <p className="text-xs text-zinc-500">AI is identifying foods and estimating nutrition</p>
                  <Loader2 className="h-6 w-6 text-purple-500 mx-auto mt-4 animate-spin" />
                </div>
              )}

              {/* Step 3: Review */}
              {step === 'review' && analysis && (
                <div className="space-y-4">
                  {/* Photo thumbnail */}
                  {previewUrl && (
                    <div className="flex items-center gap-3 bg-zinc-800/50 rounded-lg p-3">
                      <img src={previewUrl} alt="" className="w-16 h-16 object-cover rounded-lg" />
                      <div>
                        <p className="text-white font-medium">{analysis.meal_description}</p>
                        {analysis.suggestions && (
                          <p className="text-xs text-green-400 mt-1">💡 {analysis.suggestions}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Detected Foods */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-white">Detected Foods</h4>
                      <button
                        onClick={addManualFood}
                        className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" /> Add Item
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      {editedFoods.map((food, idx) => (
                        <div key={idx} className="bg-zinc-800/50 rounded-lg p-3">
                          <div className="flex items-start justify-between mb-2">
                            <input
                              type="text"
                              value={food.name}
                              onChange={(e) => updateFood(idx, 'name', e.target.value)}
                              className="bg-transparent text-white font-medium border-b border-transparent hover:border-zinc-600 focus:border-purple-500 outline-none"
                            />
                            <button
                              onClick={() => removeFood(idx)}
                              className="text-zinc-500 hover:text-red-500"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          <p className="text-xs text-zinc-500 mb-2">{food.portion_size}</p>
                          <div className="grid grid-cols-4 gap-2 text-xs">
                            <div>
                              <label className="text-zinc-500 block">Cal</label>
                              <input
                                type="number"
                                value={food.calories}
                                onChange={(e) => updateFood(idx, 'calories', e.target.value)}
                                className="w-full bg-zinc-700 rounded px-2 py-1 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-zinc-500 block">Protein</label>
                              <input
                                type="number"
                                value={food.protein}
                                onChange={(e) => updateFood(idx, 'protein', e.target.value)}
                                className="w-full bg-zinc-700 rounded px-2 py-1 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-zinc-500 block">Carbs</label>
                              <input
                                type="number"
                                value={food.carbs}
                                onChange={(e) => updateFood(idx, 'carbs', e.target.value)}
                                className="w-full bg-zinc-700 rounded px-2 py-1 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-zinc-500 block">Fat</label>
                              <input
                                type="number"
                                value={food.fat}
                                onChange={(e) => updateFood(idx, 'fat', e.target.value)}
                                className="w-full bg-zinc-700 rounded px-2 py-1 text-white"
                              />
                            </div>
                          </div>
                          {food.confidence && food.confidence !== 'manual' && (
                            <span className={`inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full ${
                              food.confidence === 'high' ? 'bg-green-500/20 text-green-400' :
                              food.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {food.confidence} confidence
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-white mb-3">Meal Totals</h4>
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div>
                        <div className="text-xl font-bold text-white">{Math.round(totals.calories)}</div>
                        <div className="text-xs text-zinc-400">Calories</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-red-400">{totals.protein.toFixed(1)}g</div>
                        <div className="text-xs text-zinc-400">Protein</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-yellow-400">{totals.carbs.toFixed(1)}g</div>
                        <div className="text-xs text-zinc-400">Carbs</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-green-400">{totals.fat.toFixed(1)}g</div>
                        <div className="text-xs text-zinc-400">Fat</div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={resetState}
                      className="flex-1 border-zinc-700"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Retake
                    </Button>
                    <Button
                      onClick={saveMeal}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Log Meal
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Saving */}
              {step === 'saving' && (
                <div className="py-12 text-center">
                  <Loader2 className="h-8 w-8 text-purple-500 mx-auto mb-4 animate-spin" />
                  <p className="text-white">Saving your meal...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
